/**
 * Validates pricing rules for correctness and consistency
 * Catches errors early rather than at calculation time
 * Prevents code injection attacks via expression sanitization
 */

export class PricingRulesValidator {
  /**
   * Validate a single rule object
   * Checks for required fields and valid structure
   * @param {object} rule - Rule object to validate
   * @param {string} category - Rule category name
   * @param {string} type - Rule type name
   * @throws {InvalidRuleError} If rule is invalid
   * @returns {boolean} True if valid
   */
  static validateRule(rule, category, type) {
    // Check required fields
    if (!rule) {
      throw new InvalidRuleError(
        `Rule for ${category}.${type} is null or undefined`
      );
    }

    if (typeof rule !== 'object') {
      throw new InvalidRuleError(
        `Rule for ${category}.${type} must be an object, got ${typeof rule}`
      );
    }

    // Validate conditions if present
    if (rule.conditions && Array.isArray(rule.conditions)) {
      this.validateConditions(rule.conditions, category, type);
    }

    // Validate baseFormula if present
    if (rule.baseFormula && typeof rule.baseFormula !== 'string') {
      throw new InvalidRuleError(
        `baseFormula for ${category}.${type} must be a string`
      );
    }

    return true;
  }

  /**
   * Validate conditions array in a rule
   * Each condition must have: if (expression), apply (type), value (parameter)
   * Warns if both discount and surcharge conditions in same rule
   * @param {array} conditions - Array of condition objects
   * @param {string} category - Rule category name
   * @param {string} type - Rule type name
   * @throws {InvalidRuleError} If conditions array is invalid
   * @returns {undefined}
   */
  static validateConditions(conditions, category, type) {
    if (!Array.isArray(conditions)) {
      throw new InvalidRuleError(
        `Conditions for ${category}.${type} must be an array`
      );
    }

    const conflictingApplyTypes = new Set();

    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i];

      if (!condition || typeof condition !== 'object') {
        throw new InvalidRuleError(
          `Condition[${i}] for ${category}.${type} must be an object`
        );
      }

      // Validate required fields
      if (!condition.if) {
        throw new InvalidRuleError(
          `Condition[${i}] for ${category}.${type} missing 'if' field`
        );
      }

      if (!condition.apply) {
        throw new InvalidRuleError(
          `Condition[${i}] for ${category}.${type} missing 'apply' field`
        );
      }

      // Validate apply type
      const validApplyTypes = ['discount', 'surcharge', 'formula'];
      if (!validApplyTypes.includes(condition.apply)) {
        throw new InvalidRuleError(
          `Condition[${i}] for ${category}.${type} has invalid 'apply' type: ${condition.apply}. Must be one of: ${validApplyTypes.join(', ')}`
        );
      }

      // For discount/surcharge, validate value
      if (
        (condition.apply === 'discount' || condition.apply === 'surcharge') &&
        (typeof condition.value !== 'number' || condition.value < 0)
      ) {
        throw new InvalidRuleError(
          `Condition[${i}] for ${category}.${type} has invalid 'value': must be a number >= 0`
        );
      }

      // For formula, validate format
      if (
        condition.apply === 'formula' &&
        typeof condition.value !== 'string'
      ) {
        throw new InvalidRuleError(
          `Condition[${i}] for ${category}.${type} formula 'value' must be a string`
        );
      }

      conflictingApplyTypes.add(condition.apply);

      if (
        conflictingApplyTypes.has('discount') &&
        conflictingApplyTypes.has('surcharge')
      ) {
        console.warn(
          `Rule ${category}.${type} has both discount and surcharge conditions. The last matching condition will win.`
        );
      }
    }
  }

  /**
   * Validate entire rules object recursively
   * Checks all categories and types for valid structure
   * @param {object} rules - Complete rules object to validate
   * @throws {InvalidRuleError} If rules structure is invalid
   * @returns {boolean} True if valid
   */
  static validateRules(rules) {
    if (!rules || typeof rules !== 'object') {
      throw new InvalidRuleError('Rules must be an object');
    }

    for (const category in rules) {
      const categoryRules = rules[category];

      if (!categoryRules || typeof categoryRules !== 'object') {
        throw new InvalidRuleError(
          `Category '${category}' rules must be an object`
        );
      }

      for (const type in categoryRules) {
        const rule = categoryRules[type];
        this.validateRule(rule, category, type);
      }
    }

    return true;
  }

  /**
   * Validate condition expression for safe operators
   * Prevents code injection by allowing only comparison/logic operators
   * Allowed: alphanumeric, ._  ><=!&|()
   * @param {string} condition - Condition expression (e.g., "quantity > 100")
   * @throws {InvalidRuleError} If condition contains unsafe characters
   * @returns {boolean} True if safe
   */
  static isConditionSafe(condition) {
    if (typeof condition !== 'string') {
      throw new InvalidRuleError('Condition must be a string');
    }

    // Only allow alphanumeric, operators, and parentheses
    const safePattern = /^[a-zA-Z0-9._\s><=!&|()]*$/;
    if (!safePattern.test(condition)) {
      throw new InvalidRuleError(
        `Condition contains unsafe characters: ${condition}`
      );
    }

    return true;
  }

  /**
   * Validate formula expression for safe math operators
   * Prevents code injection by allowing only math operations
   * Allowed: alphanumeric, ._ +−*\/()
   * @param {string} formula - Formula expression (e.g., "unitPrice * 0.9")
   * @throws {InvalidRuleError} If formula contains unsafe characters
   * @returns {boolean} True if safe
   */
  static isFormulaSafe(formula) {
    if (typeof formula !== 'string') {
      throw new InvalidRuleError('Formula must be a string');
    }

    // Only allow alphanumeric, math operators, and parentheses
    const safePattern = /^[a-zA-Z0-9._\s+\-*/()]*$/;
    if (!safePattern.test(formula)) {
      throw new InvalidRuleError(
        `Formula contains unsafe characters: ${formula}`
      );
    }

    return true;
  }
}

/**
 * Custom error class for invalid pricing rules
 * Thrown when rule validation fails
 */
export class InvalidRuleError extends Error {
  /**
   * Create a new InvalidRuleError
   * @param {string} message - Error description
   */
  constructor(message) {
    super(message);
    this.name = 'InvalidRuleError';
  }
}

export default {
  PricingRulesValidator,
  InvalidRuleError,
};
